import { useState } from "react";
import { DateTime } from "luxon";
import { DataI } from "../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { PolicyI } from "../../../../../api/request/Lead/Interfaces/PolicyInterface";
import { PricingI } from "../../../../../api/request/Lead/Interfaces/PricingInterface";
import { BeneficiaryI } from "../../../../../api/request/Lead/Interfaces/BeneficiaryInterface";
import { DocumentsI } from "../../../../../api/request/Lead/Interfaces/DocumentsInterface";
import { BeneficiaryDataI } from "../../../../../api/request/Beneficiaries/Interfaces/BeneficiariesInterfaces";
import { DOMAIN } from "../../../../../constants/Global";
import RetentionFlow from "../components/RetentionFlow";
import { currentPremiumOf } from "../components/pricing/dashboardPrice";
import CancelPolicyLink from "../components/myPolicy/CancelPolicyLink";
import WithdrawalNotice from "../components/myPolicy/WithdrawalNotice";
import {
  PolicyHeader,
  CoverageSummaryCard,
  BeneficiariesCard,
  DocumentsCard,
  ExclusionsCard,
} from "../components/myPolicy";

interface MyPolicyI {
  userData?: DataI;
  policy?: PolicyI;
  pricing?: PricingI;
  beneficiaries?: BeneficiaryI[];
  documents?: DocumentsI | null;
  beneficiaryIT: { type: string; nominative_beneficiary: number | null };
  domain?: string;
  resumeBeneficiaryIT?: BeneficiaryDataI[];
  navigateToRoute: (route: string) => void;
}

const MyPolicy = ({
  userData,
  policy,
  pricing,
  beneficiaries,
  documents,
  resumeBeneficiaryIT,
  navigateToRoute,
}: MyPolicyI) => {
  const [showRetention, setShowRetention] = useState(false);

  const policyNumber = policy?.policyNumber || userData?.policyNumber || "—";
  const capital = policy?.traits?.find((t) => t.trait === "DEATH")?.capital ?? 0;

  // IT recesso window (D.lgs. 209/2025): the withdrawal path is offered only
  // while the policy is within the first 30 days from the contract signature.
  // After that, the normal cancellation/retention link is shown instead.
  const signedAt = policy?.signatureDate
    ? DateTime.fromFormat(policy.signatureDate, "dd/MM/yyyy")
    : undefined;
  const isWithinWithdrawalWindow =
    !!signedAt &&
    signedAt.isValid &&
    DateTime.now() <= signedAt.plus({ days: 30 }).endOf("day");
  const showWithdrawal = DOMAIN === "it" && isWithinWithdrawalWindow;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 900 }}>
      <PolicyHeader policyNumber={policyNumber} />

      <CoverageSummaryCard policy={policy} capital={capital} />

      <BeneficiariesCard
        beneficiaries={beneficiaries}
        resumeBeneficiaryIT={resumeBeneficiaryIT}
        navigateToRoute={navigateToRoute}
      />

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24 }}
        className="md:!grid-cols-2"
      >
        <DocumentsCard
          contractURL={documents?.contractUrl || undefined}
          durURL={documents?.dur || undefined}
          certificateURL={documents?.certificateUrl || undefined}
        />
        <ExclusionsCard />
      </div>

      {showWithdrawal ? (
        <WithdrawalNotice />
      ) : (
        <CancelPolicyLink onCancel={() => setShowRetention(true)} />
      )}

      {showRetention && (
        <RetentionFlow
          currentCapital={capital}
          currentPremium={currentPremiumOf(pricing)}
          beneficiaryNames={
            beneficiaries?.map((b) => b.Nombre).filter(Boolean) || []
          }
          onClose={() => setShowRetention(false)}
        />
      )}
    </div>
  );
};

export default MyPolicy;
